import React from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Gutter,
  Layout,
  SidedrawerFooter,
  SidedrawerHeader,
  SidedrawerSection,
  SidedrawerUtil,
  SmallHeader,
  useApi,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';

import { WorkFeedFilterUtil } from '../../utils/work-feed-filter-util';
import { WorkFeedApi } from '../../api/work-feed-api';
import { WorkFeedFiltersForm } from '../WorkFeedFiltersForm/WorkFeedFiltersForm';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { UserApi } from '../../api/user-api';

export type WorkFeedFiltersSidedrawerProps = {
  candidate: Candidate;
};

export const WorkFeedFiltersSidedrawer = ({
  candidate,
}: WorkFeedFiltersSidedrawerProps) => {
  const [{ data: user }, { data: taxonomy }] = useApi(
    UserApi.readUser(),
    TaxonomyApi.readTaxonomy()
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // NOTE: useParams() is unavailable here because SidedrawerUtil.show() renders
  // this component outside the <Routes> tree, so route params cannot be resolved.
  const talentBasePath = user.candidateId
    ? `/talent`
    : `/talent/${candidate.id}`;

  const getFilters = () =>
    WorkFeedFilterUtil.getFilters(searchParams, candidate, taxonomy);

  const getSearchFilters = () => {
    return {
      page: 1,
      pageSize: 10,
      filters: getFilters(),
    };
  };

  const [search] = useApi(
    WorkFeedApi.readWorkFeed(candidate.id, getSearchFilters())
  );

  return (
    <>
      <SidedrawerHeader
        onClose={() => {
          SidedrawerUtil.remove('work-feed-filters-side-drawer');
        }}
        name="WORK_FEED_FILTERS_SIDEDRAWER_HEADER"
      >
        <Layout position="middle space-between">
          <Gutter right="16px">
            <SmallHeader name="WORK_FEED_FILTERS_SIDEDRAWER_TITLE">
              Filters
            </SmallHeader>
          </Gutter>
          <Button
            name="WORK_FEED_FILTERS_CONFIGURE_ALERTS"
            pattern="secondary"
            variation="outline"
            to={`${talentBasePath}/preferences/communications?scrollTo=alerts`}
            onClick={() => {
              SidedrawerUtil.remove('work-feed-filters-side-drawer');
            }}
          >
            Configure Alerts
          </Button>
        </Layout>
      </SidedrawerHeader>
      <SidedrawerSection>
        <WorkFeedFiltersForm
          name="WORK_FEED_FILTERS_FORM"
          candidate={candidate}
          filters={getFilters()}
        />
      </SidedrawerSection>
      <SidedrawerFooter>
        <Button
          name="CLEAR_ALL"
          variation="outline"
          onClick={() => {
            WorkFeedFilterUtil.applyFilters(
              setSearchParams,
              candidate,
              taxonomy,
              null
            );
          }}
        >
          Clear All
        </Button>
        <Button
          onClick={() => {
            SidedrawerUtil.remove('work-feed-filters-side-drawer');
          }}
          name="SHOW"
        >
          Show {search.meta.resultCount}
        </Button>
      </SidedrawerFooter>
    </>
  );
};
